import { useAuth } from './useAuth';

// Regular `admin` gets everything master gets — EXCEPT the two sections
// that let a person modify or observe other privileged accounts:
//   - manage_admins       (Admins section — create/edit/delete admins)
//   - view_admin_activity (audit trail of what admins/operators did)
// Both stay master-only. Operators keep their existing narrower access.
const PERMISSIONS = {
  // Admin Management — master-only (regular admin can't see this section)
  manage_admins: ['admin_master'],

  // Dashboard & Overview
  view_dashboard: ['admin_master', 'admin'],

  // Orders Management
  view_orders: ['admin_master', 'admin', 'operator'],
  manage_orders: ['admin_master', 'admin', 'operator'],
  cancel_orders: ['admin_master', 'admin'],

  // Customers Management
  view_customers: ['admin_master', 'admin', 'operator'],
  manage_customers: ['admin_master', 'admin', 'operator'],
  ban_customers: ['admin_master', 'admin', 'operator'],

  // Users Management
  view_users: ['admin_master', 'admin'],
  manage_users: ['admin_master', 'admin'],

  // Drivers Management
  view_drivers: ['admin_master', 'admin', 'operator'],
  manage_drivers: ['admin_master', 'admin', 'operator'],

  // Agents Management
  view_agents: ['admin_master', 'admin'],
  manage_agents: ['admin_master', 'admin'],

  // Transactions
  view_transactions: ['admin_master', 'admin'],

  // Ratings Management
  view_ratings: ['admin_master', 'admin', 'operator'],
  manage_ratings: ['admin_master', 'admin'],

  // Reports
  view_reports: ['admin_master', 'admin'],

  // Delete Requests
  view_delete_requests: ['admin_master', 'admin', 'operator'],
  manage_delete_requests: ['admin_master', 'admin'],

  // Blocked Numbers
  manage_blocked_numbers: ['admin_master', 'admin', 'operator'],

  // Notifications
  send_notifications: ['admin_master', 'admin', 'operator'],
  delete_notifications: ['admin_master', 'admin'],

  // Driver broadcasts & banners — admin-only (operator does not get these)
  manage_broadcasts: ['admin_master', 'admin'],
  manage_banners: ['admin_master', 'admin'],

  // Map Tracking
  view_map: ['admin_master', 'admin', 'operator'],

  // Security Logs — admins can now see them too.
  view_security_logs: ['admin_master', 'admin'],

  // Settings
  view_settings: ['admin_master', 'admin'],
  manage_settings: ['admin_master', 'admin'],

  // App Versions section
  manage_app_versions: ['admin_master', 'admin'],

  // Admin Activity audit trail — master-only (regular admin can't see it)
  view_admin_activity: ['admin_master'],

  // Audit Logs
  view_audit_logs: ['admin_master', 'admin'],
};

export function usePermissions() {
  const { user } = useAuth();

  const hasPermission = (permission) => {
    if (!user) return false;
    
    const allowedRoles = PERMISSIONS[permission];
    if (!allowedRoles) return false;
    
    return allowedRoles.includes(user.type);
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions) => {
    return permissions.every(permission => hasPermission(permission));
  };

  const canViewPage = (pageName) => {
    const pagePermissionMap = {
      dashboard: 'view_dashboard',
      orders: 'view_orders',
      customers: 'view_customers',
      users: 'view_users',
      drivers: 'view_drivers',
      agents: 'view_agents',
      transactions: 'view_transactions',
      ratings: 'view_ratings',
      reports: 'view_reports',
      'delete-requests': 'view_delete_requests',
      'blocked-numbers': 'manage_blocked_numbers',
      notifications: 'send_notifications',
      broadcasts: 'manage_broadcasts',
      banners: 'manage_banners',
      map: 'view_map',
      'security-logs': 'view_security_logs',
      settings: 'view_settings',
      'app-versions': 'manage_app_versions',
      'admin-activity': 'view_admin_activity',
      'audit-logs': 'view_audit_logs',
      admins: 'manage_admins'
    };

    const permission = pagePermissionMap[pageName];
    return permission ? hasPermission(permission) : false;
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canViewPage,
  };
}